import type { InputEvent, ReactElement } from 'react';
import {
  FieldError,
  Input,
  Label,
  Text,
  TextArea,
  TextField as AriaTextField,
  type TextFieldProps as AriaTextFieldProps,
} from 'react-aria-components';
import { cx } from './cx';
import s from './fields.module.css';

export interface TextFieldProps extends Omit<AriaTextFieldProps, 'children' | 'className'> {
  /** Visible label (every input has one). */
  label: string;
  /** Help text under the input. */
  description?: string;
  /** Message shown when the field is invalid. */
  errorMessage?: string;
  /** Multi-line input. */
  multiline?: boolean;
  placeholder?: string;
  /** Keep the label for screen readers only, where a column heading already names the input. */
  labelHidden?: boolean;
}

/**
 * React Aria shows or clears a field's error only when the value is committed: on submit, or on
 * the native `change` a text field fires when it loses focus. Pressing the button under a field
 * takes that focus at mousedown, so a fixed error used to vanish between mousedown and mouseup;
 * the form got shorter, the button moved out from under the pointer, and the click missed (the
 * first Confirm after an empty reason did nothing). Once an error shows, each keystroke commits
 * the value, so the error clears as soon as it's fixed and nothing moves when the person clicks.
 */
export function commitWhileInvalid(
  event: InputEvent<HTMLInputElement | HTMLTextAreaElement>,
): void {
  const input = event.currentTarget;
  if (input.getAttribute('aria-invalid') === 'true') input.dispatchEvent(new Event('change'));
}

/** A labelled text input (or text area) with description and error message. */
export function TextField({
  label,
  description,
  errorMessage,
  multiline = false,
  placeholder,
  isRequired,
  labelHidden = false,
  ...rest
}: TextFieldProps): ReactElement {
  // A caller that sets `isInvalid` decides when its error shows; committing early changes nothing.
  const inputProps = {
    ...(placeholder !== undefined && { placeholder }),
    ...(rest.isInvalid === undefined && { onInput: commitWhileInvalid }),
  };
  return (
    <AriaTextField {...rest} isRequired={isRequired ?? false} className={s.field}>
      <Label className={labelHidden ? 'visually-hidden' : s.label}>
        {label}
        {isRequired === true && (
          <span className={s.required} aria-hidden="true">
            *
          </span>
        )}
      </Label>
      <div className={s.body}>
        {multiline ? (
          <TextArea className={cx(s.input, s.textarea)} {...inputProps} />
        ) : (
          <Input className={s.input} {...inputProps} />
        )}
        {description !== undefined && (
          <Text slot="description" className={s.description}>
            {description}
          </Text>
        )}
        <FieldError className={s.error}>{errorMessage}</FieldError>
      </div>
    </AriaTextField>
  );
}
